/** When placed in the 'default' branch of a switch statement, this will throw a compile error if all cases of the switch
 * are not handled. At runtime, nothing will happen. e.g.
 * const errorCode: 400|401|403 = 400;
 * switch (errorCode) {
 *   case 400:
 *     return "BadRequest";
 *   case 401:
 *     return "Unauthorized";
 *   defaut:
 *     exhaustiveCheck(errorCode); // This will be a compile time error. At run time, nothing will happen.
 * }
 */
export function exhaustiveCheck(_value: never): void {
  // Do nothing, this function only causes a compile time error
  //
  // If we have cases where an exhaustive check should throw at runtime, we can either add a parameter or
  // create a similar function that takes the message to throw, and returns type 'never'
}

/**
 * Returns the value if it matches one of the provided options, otherwise returns undefined.
 */
export function oneOfOrUndefined<T extends string>(
  value: string | undefined,
  ...validValues: T[]
): T | undefined {
  if (validValues.includes(value as T)) {
    return value as T;
  }
  return undefined;
}

/** Used by MustBeEnumLike, and not intended to be exported for now */
type MustBeNonEmptyObject<T> = keyof T extends never ? never : T;
/** Used by MustBeEnumLike, and not intended to be exported for now */
type MustBeNonPrototype<T> = T extends { prototype: any } ? never : T;

/** Used to constrain a type T to things that could be TypeScript enums. This doesn't work perfectly, but catches most errors
 */
export type MustBeEnumLike<T> = T extends
  | string
  | number
  | Function
  | undefined
  | null
  | any[]
  ? never
  : MustBeNonEmptyObject<MustBeNonPrototype<T>>;

/** Checks that the second param has keys that are a subset of the first param
 *    ensureSubsetOfKeys({ a: 0, b: 1, c: 2 }, { a: 0 }); // success
 *    ensureSubsetOfKeys({ a: 0, b: 1, c: 2 }, { a: "string" }); // success
 *    ensureSubsetOfKeys({ a: 0, b: 1, c: 2 }, { a: "string", b: { d: true} }); // success
 *    ensureSubsetOfKeys({}, {}); // success
 *    ensureSubsetOfKeys({ a: 0, b: 1, c: 2 }, { x: 0 }); // fails, x is a new property in second arg
 *    ensureSubsetOfKeys({ a: 0, b: 1, c: 2 }, { a: "string", b: { d: true }, x: { e: false } }); // fails, x is a new property in second arg
 *    ensureSubsetOfKeys({}, { x: 0 }); // fails, x is a new property in second arg
 */
export function ensureSubsetOfKeys<
  T,
  U extends Partial<Record<keyof T, unknown>>,
>(
  _fullObject: T,
  subsetObject: U & Record<Exclude<keyof U, keyof T>, never>,
): U {
  // This function does nothing at runtime; it exists purely for type-checking.
  return subsetObject;
}

/** When given an object and a key, causes a compile error if the key is not a valid key for the object, otherwise the key is returned */
export function ensureKey<T>(_obj: T | null | undefined, key: keyof T) {
  return key;
}

/** Represents a type for a value that is not null nor undefined. e.g. use as below to ensure that the values in a
 * Record are not null nor undefined:
 *
 * return { somestuff: abcd } satisfies Record<string, NotNullish>;
 *
 * The 'object' type covers most things, e.g. functions, arrays */
export type NotNullish = string | boolean | number | bigint | object | symbol;

/** Stringifies all of an object's properties, even the ones that are not enumerable, such as the properties in
 * a JavaScript Error/exception
 */
export function stringifyAllForDebugging(
  obj: Record<string | symbol, any> | undefined | null,
) {
  if (obj === undefined) {
    return undefined;
  }
  if (obj === null) {
    return JSON.stringify(obj);
  }
  try {
    const allKeys = Reflect.ownKeys(obj);

    const fullCopy: Record<string | symbol, any> = {};
    for (const key of allKeys) {
      const value = obj[key];

      // JSON can't handle functions, undefined, or symbols as values
      if (typeof value === 'function') {
        fullCopy[key] = '[Function]';
      } else if (value === undefined) {
        fullCopy[key] = '[Undefined]';
      } else if (typeof value === 'symbol') {
        fullCopy[key] = value.toString();
      } else {
        fullCopy[key] = value;
      }
    }

    return JSON.stringify(fullCopy, null, 2);
  } catch (e) {
    return (
      'Error in stringifyAllForDebugging, normal JSON.stringify is ' +
      JSON.stringify(e)
    );
  }
}
